import React, { useState } from "react";
import { Box, Table, Badge, Text, Flex, Button } from "@radix-ui/themes";
import { CircularArrowUp, CircularArrowDown } from "./circular-arrows";
import MiniPerformanceChart from "./mini-performance-chart";

export interface ContributionData {
  contribution_to_return: number;
  weight: number;
  performance_1m?: number | null; // Optional 1-month performance data
  timeseries?: Array<{ datetime: string; price: number; percent_move: number }>; // Optional timeseries for chart
}

type SortBy = "contribution" | "weight" | "name" | "performance";
type SortDirection = "asc" | "desc";

export interface ContributionsTableProps {
  data: Record<string, ContributionData>;
  headers: string[];
  displayNameLookup?: Record<string, string>;
  title?: string;
  showTitle?: boolean;
  compact?: boolean;
  sortable?: boolean;
  clickableRows?: boolean;
  selectedRow?: string;
  onRowClick?: (key: string) => void;
  positiveExplanationCounts?: Record<string, number>;
  negativeExplanationCounts?: Record<string, number>;
  expandedContent?: Record<string, React.ReactNode>;
  expandedRowKey?: string;
  readOnly?: boolean;
  onAnimationComplete?: () => void;
  showCharts?: boolean; // Show mini performance charts
  timeframe?: string; // Timeframe label for charts
}

export function ContributionsTable({
  data,
  headers,
  displayNameLookup,
  title,
  showTitle = false,
  compact = false,
  sortable = false,
  clickableRows = false,
  selectedRow,
  onRowClick,
  positiveExplanationCounts,
  negativeExplanationCounts,
  expandedContent,
  expandedRowKey,
  readOnly = false,
  showCharts = false,
  timeframe = "1M",
}: ContributionsTableProps) {
  const [sortBy, setSortBy] = useState<SortBy>("contribution");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  const handleSort = (newSortBy: SortBy) => {
    if (sortBy === newSortBy) {
      setSortDirection(sortDirection === "desc" ? "asc" : "desc");
    } else {
      setSortBy(newSortBy);
      setSortDirection("desc");
    }
  };
  const sortedEntries = Object.entries(data).sort(([keyA, a], [keyB, b]) => {
    let comparison = 0;

    switch (sortBy) {
      case "contribution":
        comparison =
          Math.abs(b.contribution_to_return) -
          Math.abs(a.contribution_to_return);
        break;
      case "weight":
        comparison = b.weight - a.weight;
        break;
      case "performance": {
        const perfA = a.performance_1m ?? -Infinity;
        const perfB = b.performance_1m ?? -Infinity;
        comparison = perfB - perfA;
        break;
      }
      case "name": {
        const nameA = displayNameLookup
          ? displayNameLookup[keyA] || keyA
          : keyA;
        const nameB = displayNameLookup
          ? displayNameLookup[keyB] || keyB
          : keyB;
        comparison = nameA.localeCompare(nameB);
        break;
      }
      default:
        return 0;
    }

    return sortDirection === "desc" ? comparison : -comparison;
  });

  return (
    <Box className={compact ? "mb-3" : "mb-4"}>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          /* Remove border between main row and expanded content */
          .expanded-row-no-border {
            box-shadow: none !important;
            border-top: none !important;
          }

          .expanded-row-no-border .rt-TableCell {
            box-shadow: none !important;
            border-top: none !important;
          }
        `}
      </style>

      {showTitle && title && (
        <Text size="2" weight="bold" className="mb-2">
          {title}
        </Text>
      )}

      <Table.Root
        size={compact ? "1" : "2"}
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <Table.Header>
          <Table.Row>
            {headers.map((header, index) => {
              const hasPerformance = Object.values(data).some(
                (entry) => entry.performance_1m !== undefined
              );
              const numColumns = hasPerformance ? 4 : 3;

              const isSortable =
                !readOnly &&
                sortable &&
                (index === 0 || index === 1 || index === 2 || index === 3);

              const getSortKey = (): SortBy => {
                if (index === 0) return "name";
                if (index === 1) return "contribution";
                if (index === 2) return "performance";
                if (index === 3) return "weight";
                return "contribution";
              };

              const getColumnWidth = () => {
                if (numColumns === 4) {
                  // With performance column
                  if (index === 0) return "40%";
                  if (index === 1) return "20%";
                  if (index === 2) return "20%";
                  if (index === 3) return "20%";
                } else {
                  // Without performance column
                  if (index === 0) return "50%";
                  if (index === 1) return "25%";
                  if (index === 2) return "25%";
                }
                return "auto";
              };

              return (
                <Table.ColumnHeaderCell
                  key={header}
                  justify={index > 0 ? "end" : "start"}
                  style={{ width: getColumnWidth() }}
                >
                  {isSortable ? (
                    <Button
                      variant="ghost"
                      size={compact ? "1" : "2"}
                      onClick={() => handleSort(getSortKey())}
                      style={{
                        padding: compact ? "2px 4px" : "4px 8px",
                        height: "auto",
                        minHeight: "auto",
                      }}
                    >
                      <Flex align="center" gap="1">
                        <Text size={compact ? "1" : "2"} weight="medium">
                          {header}
                        </Text>
                        <Box style={{ width: "12px", textAlign: "center" }}>
                          {sortBy === getSortKey() ? (
                            <Text size={compact ? "1" : "2"}>
                              {sortDirection === "desc" ? "↓" : "↑"}
                            </Text>
                          ) : null}
                        </Box>
                      </Flex>
                    </Button>
                  ) : (
                    <Text size={compact ? "1" : "2"} weight="medium">
                      {header}
                    </Text>
                  )}
                </Table.ColumnHeaderCell>
              );
            })}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {sortedEntries.map(([key, entry]) => {
            const displayName = displayNameLookup
              ? displayNameLookup[key] || key
              : key;
            const contribution = entry.contribution_to_return;
            const weight = entry.weight;
            const performance = entry.performance_1m;
            const hasPerformance = performance !== undefined;
            const isSelected = selectedRow === key;
            const isExpanded = expandedRowKey === key;
            const positiveCount = positiveExplanationCounts?.[key] || 0;
            const negativeCount = negativeExplanationCounts?.[key] || 0;

            return (
              <React.Fragment key={key}>
                <Table.Row
                  style={
                    {
                      cursor:
                        clickableRows && !readOnly ? "pointer" : "default",
                      backgroundColor: isSelected
                        ? "var(--accent-3)"
                        : undefined,
                      borderLeft: isSelected
                        ? "3px solid var(--accent-9)"
                        : "3px solid transparent",
                      "--table-row-box-shadow": readOnly
                        ? "none"
                        : "inset 0 -1px var(--gray-a5)",
                    } as React.CSSProperties
                  }
                  onClick={
                    clickableRows && !readOnly && onRowClick
                      ? () => onRowClick(key)
                      : undefined
                  }
                >
                  <Table.Cell
                    style={{
                      width: hasPerformance ? "40%" : "50%",
                      verticalAlign: "middle",
                      padding: "12px 8px",
                    }}
                  >
                    <Flex align="center" gap="2" justify="start">
                      <Text size={compact ? "1" : "2"} weight="medium">
                        {displayName}
                      </Text>
                      {(positiveCount > 0 || negativeCount > 0) && (
                        <Flex gap="2" align="center">
                          {positiveCount > 0 && (
                            <Flex align="center" gap="1">
                              <CircularArrowUp size={14} />
                              <Text size="1" color="green">
                                {positiveCount}
                              </Text>
                            </Flex>
                          )}
                          {negativeCount > 0 && (
                            <Flex align="center" gap="1">
                              <CircularArrowDown size={14} />
                              <Text size="1" color="red">
                                {negativeCount}
                              </Text>
                            </Flex>
                          )}
                        </Flex>
                      )}
                    </Flex>
                  </Table.Cell>

                  <Table.Cell
                    justify="end"
                    style={{
                      width: hasPerformance ? "20%" : "25%",
                      verticalAlign: "middle",
                      padding: "12px 8px",
                    }}
                  >
                    <Flex align="center" justify="end">
                      <Badge
                        variant="surface"
                        size={compact ? "1" : "2"}
                        color={contribution >= 0 ? "green" : "red"}
                      >
                        {contribution >= 0 ? "+" : ""}
                        {(contribution * 100).toFixed(2)}
                      </Badge>
                    </Flex>
                  </Table.Cell>

                  {hasPerformance && (
                    <Table.Cell
                      justify="end"
                      style={{
                        width: "20%",
                        verticalAlign: "middle",
                        padding: "12px 8px",
                      }}
                    >
                      <Flex align="center" justify="end">
                        {performance === null ? (
                          <Text size={compact ? "1" : "2"} color="gray">
                            —
                          </Text>
                        ) : (
                          <Badge
                            variant="surface"
                            size={compact ? "1" : "2"}
                            color={performance >= 0 ? "green" : "red"}
                          >
                            {performance >= 0 ? "+" : ""}
                            {performance.toFixed(2)}
                          </Badge>
                        )}
                      </Flex>
                    </Table.Cell>
                  )}

                  <Table.Cell
                    justify="end"
                    style={{
                      width: hasPerformance ? "20%" : "25%",
                      verticalAlign: "middle",
                      padding: "12px 8px",
                    }}
                  >
                    <Flex align="center" justify="end">
                      <Badge
                        variant="outline"
                        size={compact ? "1" : "2"}
                        color="gray"
                      >
                        {(weight * 100).toFixed(1)}
                      </Badge>
                    </Flex>
                  </Table.Cell>
                </Table.Row>

                {!readOnly &&
                  (isExpanded || expandedRowKey === "all") &&
                  expandedContent?.[key] && (
                    <Table.Row>
                      <Table.Cell
                        colSpan={hasPerformance ? 4 : 3}
                        style={{
                          padding: 0,
                          animation: "fadeIn 0.3s ease-out",
                          boxShadow: "none",
                          borderTop: "none",
                        }}
                      >
                        {showCharts &&
                        entry.timeseries &&
                        entry.timeseries.length > 0 ? (
                          <Box style={{ position: "relative" }}>
                            {/* Chart floats in upper right - takes up roughly half */}
                            <Box
                              style={{
                                float: "right",
                                width: "50%",
                                marginLeft: "24px",
                                paddingRight: "28px",
                                marginTop: "40px",
                                display: "flex",
                              }}
                            >
                              <Box style={{ width: "100%" }}>
                                <MiniPerformanceChart
                                  timeseries={entry.timeseries}
                                  ticker={
                                    displayNameLookup?.[key]?.split(" ")[0]
                                  }
                                  timeframe={timeframe}
                                  height={240}
                                />
                              </Box>
                            </Box>
                            <Box>{expandedContent[key]}</Box>
                          </Box>
                        ) : (
                          expandedContent[key]
                        )}
                      </Table.Cell>
                    </Table.Row>
                  )}
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}

export interface SectorContributionsTableProps
  extends Omit<ContributionsTableProps, "headers" | "title"> {
  title?: string;
  compact?: boolean;
}

export function SectorContributionsTable({
  title,
  ...props
}: SectorContributionsTableProps) {
  return (
    <ContributionsTable
      {...props}
      headers={[
        "Sector",
        "Contribution to Return (%)",
        "Weight in Portfolio (%)",
      ]}
      title={title}
      showTitle={false}
      readOnly
    />
  );
}

export interface SecurityContributionsTableProps
  extends Omit<ContributionsTableProps, "headers" | "title"> {
  title?: string;
  compact?: boolean;
}

export function SecurityContributionsTable({
  title,
  data,
  ...props
}: SecurityContributionsTableProps) {
  const numSecurities = data ? Object.keys(data).length : 0;
  const defaultTitle = `Top ${numSecurities} Notable Securities`;

  // Check if any entry has performance data
  const hasPerformance = data
    ? Object.values(data).some((entry) => entry.performance_1m !== undefined)
    : false;

  // Check if any entry has timeseries data for charts
  const hasTimeseries = data
    ? Object.values(data).some(
        (entry) => entry.timeseries && entry.timeseries.length > 0
      )
    : false;

  const headers = hasPerformance
    ? [
        "Security",
        "Contribution to Return (%)",
        "Performance 1M (%)",
        "Weight in Portfolio (%)",
      ]
    : ["Security", "Contribution to Return (%)", "Weight in Portfolio (%)"];

  return (
    <ContributionsTable
      {...props}
      data={data}
      headers={headers}
      title={title || defaultTitle}
      showTitle={true}
      sortable={true}
      clickableRows={true}
      showCharts={hasTimeseries}
      timeframe="1M"
    />
  );
}

export interface AssetClassContributionData {
  contribution_to_return: number;
  weight?: number; // Optional, not displayed
}

export interface AssetClassContributionsTableProps {
  data: Record<string, AssetClassContributionData>;
  displayNameLookup?: Record<string, string>;
  title?: string;
  compact?: boolean;
  sortable?: boolean;
}

export function AssetClassContributionsTable({
  data,
  displayNameLookup,
  title,
  compact = false,
  sortable = false,
}: AssetClassContributionsTableProps) {
  const [sortBy, setSortBy] = useState<"contribution" | "name">("contribution");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  const handleSort = (newSortBy: "contribution" | "name") => {
    if (sortBy === newSortBy) {
      setSortDirection(sortDirection === "desc" ? "asc" : "desc");
    } else {
      setSortBy(newSortBy);
      setSortDirection("desc");
    }
  };

  const sortedEntries = Object.entries(data).sort(([keyA, a], [keyB, b]) => {
    let comparison = 0;

    switch (sortBy) {
      case "contribution":
        comparison =
          Math.abs(b.contribution_to_return) -
          Math.abs(a.contribution_to_return);
        break;
      case "name": {
        const nameA = displayNameLookup
          ? displayNameLookup[keyA] || keyA
          : keyA;
        const nameB = displayNameLookup
          ? displayNameLookup[keyB] || keyB
          : keyB;
        comparison = nameA.localeCompare(nameB);
        break;
      }
      default:
        return 0;
    }

    return sortDirection === "desc" ? comparison : -comparison;
  });

  return (
    <Box className={compact ? "mb-3" : "mb-4"}>
      {title && (
        <Text size="2" weight="bold" className="mb-2">
          {title}
        </Text>
      )}

      <Table.Root
        size={compact ? "1" : "2"}
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <Table.Header>
          <Table.Row>
            {/* Asset Class column */}
            <Table.ColumnHeaderCell justify="start" style={{ width: "60%" }}>
              {sortable ? (
                <Button
                  variant="ghost"
                  size={compact ? "1" : "2"}
                  onClick={() => handleSort("name")}
                  style={{
                    padding: compact ? "2px 4px" : "4px 8px",
                    height: "auto",
                    minHeight: "auto",
                  }}
                >
                  <Flex align="center" gap="1">
                    <Text size={compact ? "1" : "2"} weight="medium">
                      Asset Class
                    </Text>
                    <Box style={{ width: "12px", textAlign: "center" }}>
                      {sortBy === "name" ? (
                        <Text size={compact ? "1" : "2"}>
                          {sortDirection === "desc" ? "↓" : "↑"}
                        </Text>
                      ) : null}
                    </Box>
                  </Flex>
                </Button>
              ) : (
                <Text size={compact ? "1" : "2"} weight="medium">
                  Asset Class
                </Text>
              )}
            </Table.ColumnHeaderCell>

            {/* Contribution to Return column */}
            <Table.ColumnHeaderCell justify="end" style={{ width: "40%" }}>
              {sortable ? (
                <Button
                  variant="ghost"
                  size={compact ? "1" : "2"}
                  onClick={() => handleSort("contribution")}
                  style={{
                    padding: compact ? "2px 4px" : "4px 8px",
                    height: "auto",
                    minHeight: "auto",
                  }}
                >
                  <Flex align="center" gap="1">
                    <Text size={compact ? "1" : "2"} weight="medium">
                      Contribution to Return
                    </Text>
                    <Box style={{ width: "12px", textAlign: "center" }}>
                      {sortBy === "contribution" ? (
                        <Text size={compact ? "1" : "2"}>
                          {sortDirection === "desc" ? "↓" : "↑"}
                        </Text>
                      ) : null}
                    </Box>
                  </Flex>
                </Button>
              ) : (
                <Text size={compact ? "1" : "2"} weight="medium">
                  Contribution to Return
                </Text>
              )}
            </Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {sortedEntries.map(([key, entry]) => {
            const displayName = displayNameLookup
              ? displayNameLookup[key] || key
              : key;
            const contribution = entry.contribution_to_return;

            return (
              <Table.Row key={key}>
                <Table.Cell
                  style={{
                    width: "60%",
                    verticalAlign: "middle",
                    padding: "12px 8px",
                  }}
                >
                  <Text size={compact ? "1" : "2"} weight="medium">
                    {displayName}
                  </Text>
                </Table.Cell>

                <Table.Cell
                  justify="end"
                  style={{
                    width: "40%",
                    verticalAlign: "middle",
                    padding: "12px 8px",
                  }}
                >
                  <Flex align="center" justify="end">
                    <Badge
                      variant="surface"
                      size={compact ? "1" : "2"}
                      color={contribution >= 0 ? "green" : "red"}
                    >
                      {contribution >= 0 ? "+" : ""}
                      {(contribution * 100).toFixed(2)}
                    </Badge>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}

export interface InlineContributionDataProps {
  weight?: number | null;
  contribution?: number | null;
  compact?: boolean;
}

export function InlineContributionData({
  weight,
  contribution,
  compact = false,
}: InlineContributionDataProps) {
  if (weight === null && contribution === null) {
    return null;
  }

  return (
    <Flex gap="2" align="center" wrap="wrap" className="justify-end">
      {contribution !== null && contribution !== undefined && (
        <Badge
          variant="surface"
          size={compact ? "1" : "2"}
          color={contribution >= 0 ? "green" : "red"}
        >
          {contribution >= 0 ? "+" : ""}
          {(contribution * 100).toFixed(2)}
        </Badge>
      )}
      {weight !== null && weight !== undefined && (
        <Badge variant="outline" size={compact ? "1" : "2"} color="gray">
          {(weight * 100).toFixed(1)}
        </Badge>
      )}
    </Flex>
  );
}

export default ContributionsTable;
